import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useBrands } from '../lib/brands.js';
import type { BrandItem } from '../lib/brands.js';
import { SenderGroups } from './SenderGroups.js';
import { ErrorNote, Field, Loading, Panel, Pill } from '../components/ui.js';

/** Odkazy hlídacího psa na stránky e-shopu; šablona může mít {token}, {race_id}, {category}, {lang}. */
const WATCHDOG_URL_FIELDS: Array<[string, string]> = [
  ['confirm', 'Potvrzení'],
  ['cancel', 'Zrušení'],
  ['cancel_all', 'Zrušit vše'],
  ['offer', 'Nabídka (záloha)'],
  ['next_season', 'Příští sezóna'],
];

const READINESS_LABEL: Record<string, string> = {
  sender_placeholder: 'e-mail odesílatele je zástupný',
  domains_placeholder: 'domény značky jsou zástupné',
  consent_controller: 'není vybraný správce souhlasů',
};

const CREDENTIALS_LABEL = {
  brand: 'Vlastní přístupy (z prostředí)',
  default: 'Společný účet SmartEmailingu',
  none: 'Bez přístupů (MockProvider)',
} as const;

export function BrandsTab({ canWrite }: { canWrite: boolean }): JSX.Element {
  const client = useQueryClient();
  const list = useBrands();
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    senderEmail: '',
    senderName: '',
    domains: '',
    languages: '',
    defaultTimezone: '',
    supportEmail: '',
    supportPhone: '',
    supportHours: '',
    frequencyCapDays: '',
    sendingEnabled: false,
    consentController: '',
    watchdogUrls: {} as Record<string, Record<string, string>>,
  });

  const save = useMutation({
    mutationFn: (code: string) =>
      api.patch(`/api/brands/${code}`, {
        name: form.name,
        senderEmail: form.senderEmail,
        senderName: form.senderName,
        domains: form.domains
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean),
        languages: form.languages
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        defaultTimezone: form.defaultTimezone,
        support: { email: form.supportEmail, phone: form.supportPhone, hours: form.supportHours },
        sendingEnabled: form.sendingEnabled,
        consentController: form.consentController || null,
        watchdogUrls: Object.fromEntries(
          Object.entries(form.watchdogUrls)
            .map(([lang, set]) => [
              lang,
              Object.fromEntries(Object.entries(set).filter(([, v]) => v.trim() !== '')),
            ])
            .filter(([, set]) => Object.keys(set as object).length > 0),
        ),
        settings: {
          frequency_cap_days: form.frequencyCapDays === '' ? null : Number(form.frequencyCapDays),
        },
      }),
    onSuccess: () => {
      setError(null);
      setEditing(null);
      void client.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const edit = (b: BrandItem): void => {
    setError(null);
    setEditing(b.code);
    setForm({
      name: b.name,
      senderEmail: b.sender.email,
      senderName: b.sender.name,
      domains: b.domains.join(', '),
      languages: b.languages.join(', '),
      defaultTimezone: b.defaultTimezone,
      supportEmail: String(b.support.email ?? ''),
      supportPhone: String(b.support.phone ?? ''),
      supportHours: String(b.support.hours ?? ''),
      frequencyCapDays: b.settings.frequencyCapDays.overridden
        ? String(b.settings.frequencyCapDays.value)
        : '',
      sendingEnabled: b.sendingEnabled,
      consentController: b.consentControllerSet ?? '',
      watchdogUrls: Object.fromEntries(
        Object.entries(b.watchdogUrls ?? {}).map(([lang, set]) => [
          lang,
          Object.fromEntries(Object.entries(set).map(([k, v]) => [k, v ?? ''])),
        ]),
      ),
    });
  };

  if (list.isLoading) return <Loading what="značky" />;
  if (list.isError) return <ErrorNote error={list.error} />;
  const set = (patch: Partial<typeof form>): void => setForm({ ...form, ...patch });

  return (
    <>
      <Panel title="Značky">
        <p className="hint">
          Každá zpráva odchází z odesílatele, šablon a přístupů své značky. Kód značky je neměnný.
          Prázdný limit četnosti znamená globální výchozí hodnotu.
        </p>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Značka</th>
                <th>Druh</th>
                <th>Odesílatel</th>
                <th>Domény</th>
                <th>Přístupy</th>
                <th>Stav</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((b) => (
                <tr key={b.code} data-testid={`brand-row-${b.code}`}>
                  <td>
                    <b>{b.code}</b> {b.name}
                  </td>
                  <td>{b.kind === 'shop' ? 'Prodej vstupenek' : 'Obsah'}</td>
                  <td>
                    {b.sender.name} &lt;{b.sender.email}&gt;
                  </td>
                  <td>{b.domains.join(', ')}</td>
                  <td>
                    {CREDENTIALS_LABEL[b.credentials]}
                    {b.sharedAccount ? (
                      <span
                        className="muted"
                        title="Jeden účet SmartEmailingu pro všechny značky; značku určuje šablona a odesílatel. Viz Nastavení → Napojení."
                      >
                        {' '}
                        (jeden účet pro všechny značky)
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {b.sendingEnabled ? (
                      <Pill tone="ok">Odesílá</Pill>
                    ) : (
                      <Pill tone="neu">Odesílání vypnuto</Pill>
                    )}
                    {!b.sendingEnabled && !b.readiness.ready ? (
                      <div className="muted" data-testid={`brand-missing-${b.code}`}>
                        Zapnout nejde:{' '}
                        {b.readiness.missing.map((k) => READINESS_LABEL[k] ?? k).join(', ')}.
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {canWrite ? (
                      <button
                        className="btn"
                        data-testid={`brand-edit-${b.code}`}
                        onClick={() => edit(b)}
                      >
                        Upravit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {editing ? (
        <Panel title={`Upravit značku ${editing}`}>
          {error ? <ErrorNote error={new Error(error)} /> : null}
          <div className="form">
            <label className="field">
              <span>Název</span>
              <input value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="field">
              <span>E-mail odesílatele</span>
              <input
                value={form.senderEmail}
                onChange={(e) => set({ senderEmail: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Jméno odesílatele</span>
              <input
                value={form.senderName}
                onChange={(e) => set({ senderName: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Domény (čárkou)</span>
              <input value={form.domains} onChange={(e) => set({ domains: e.target.value })} />
            </label>
            <label className="field">
              <span>Jazyky (čárkou)</span>
              <input value={form.languages} onChange={(e) => set({ languages: e.target.value })} />
            </label>
            <Field
              label="Správce souhlasů"
              hint="Značka, u které se evidují souhlasy zákazníků. Bez výběru nejde zapnout odesílání."
            >
              <select
                value={form.consentController}
                data-testid="brand-consent-controller"
                onChange={(e) => set({ consentController: e.target.value })}
              >
                <option value="">Nevybrán</option>
                {list.data?.items.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code === editing ? `${o.name} (sama značka)` : o.name}
                  </option>
                ))}
              </select>
            </Field>
            <label className="field">
              <span>Výchozí časové pásmo</span>
              <input
                value={form.defaultTimezone}
                onChange={(e) => set({ defaultTimezone: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Servis: e-mail</span>
              <input
                value={form.supportEmail}
                onChange={(e) => set({ supportEmail: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Servis: telefon</span>
              <input
                value={form.supportPhone}
                onChange={(e) => set({ supportPhone: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Servis: otevírací doba</span>
              <input
                value={form.supportHours}
                onChange={(e) => set({ supportHours: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Limit četnosti (dny, prázdné = výchozí)</span>
              <input
                type="number"
                min={0}
                value={form.frequencyCapDays}
                onChange={(e) => set({ frequencyCapDays: e.target.value })}
              />
            </label>
            <label className="field">
              <span>
                <input
                  type="checkbox"
                  checked={form.sendingEnabled}
                  onChange={(e) => set({ sendingEnabled: e.target.checked })}
                />{' '}
                Odesílání značky zapnuto
              </span>
            </label>
          </div>
          <h3>Hlídací pes: odkazy na stránky e-shopu</h3>
          <p className="hint">
            Stránky potvrzení, zrušení a nabídky vlastní e-shop; hub z těchto šablon skládá odkazy v
            e-mailech. Jen https na doméně značky, např.{' '}
            <code>https://www.tickets.gp/watchdog/potvrdit/&#123;token&#125;</code>. Řádek „default“
            platí pro jazyky bez vlastního řádku.
          </p>
          <div className="scroll">
            <table data-testid="watchdog-urls">
              <thead>
                <tr>
                  <th>Jazyk</th>
                  {WATCHDOG_URL_FIELDS.map(([, title]) => (
                    <th key={title}>{title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {['default', ...form.languages.split(',').map((l) => l.trim())]
                  .filter((l, i, all) => l && all.indexOf(l) === i)
                  .map((lang) => (
                    <tr key={lang}>
                      <td>
                        <b>{lang}</b>
                      </td>
                      {WATCHDOG_URL_FIELDS.map(([key, title]) => (
                        <td key={key}>
                          <input
                            aria-label={`${title} (${lang})`}
                            data-testid={`wdurl-${lang}-${key}`}
                            value={form.watchdogUrls[lang]?.[key] ?? ''}
                            onChange={(e) =>
                              set({
                                watchdogUrls: {
                                  ...form.watchdogUrls,
                                  [lang]: { ...form.watchdogUrls[lang], [key]: e.target.value },
                                },
                              })
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="filters">
            <button
              className="btn pri"
              onClick={() => save.mutate(editing)}
              disabled={save.isPending}
            >
              Uložit značku
            </button>
            <button className="btn" onClick={() => setEditing(null)}>
              Zrušit
            </button>
          </div>
        </Panel>
      ) : null}
      {editing
        ? (() => {
            const current = list.data?.items.find((b) => b.code === editing);
            return current ? <SenderGroups brand={current} canWrite={canWrite} /> : null;
          })()
        : null}
    </>
  );
}
